"use client";

import Image from "next/image";
import type { ReactNode } from "react";

import { getTopRewardsReferral } from "@/backend/focus";
import TopReferrerProfileAvatar from "@/components/bounty/top-referrer-profile-avatar";
import { Spinner } from "@/components/shared/spinner";
import { useScrollableTable } from "@/hooks/use-scrollable-table";
import { formatUSDCents } from "@/utils/currency";
import { pluralize } from "@/utils/helpers";
import { cn } from "@/utils/lib";

import { Skeleton } from "../ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableScrollable,
} from "../ui/table";

const TABLE_HEADERS = [
  { label: "Profile" },
  { label: "Total Earned" },
  { label: "Referral Bonuses" },
  { label: "Revenue Share" },
];

type TopReferrersTableProps = {
  limit?: number;
};

const Fallback = ({ children }: { children: ReactNode }) => (
  <div className="w-full overflow-hidden rounded-2xl border">
    <Table>
      <TableHeader>
        <TableRow>
          {TABLE_HEADERS.map(({ label }) => (
            <TableHead
              key={label}
              className={cn(
                `
                  cursor-pointer py-2 text-left text-xs text-muted
                  first:pl-4
                  md:text-sm
                `,
              )}
            >
              {label}
            </TableHead>
          ))}
        </TableRow>
      </TableHeader>
      <TableBody>
        <TableRow>{children}</TableRow>
      </TableBody>
    </Table>
  </div>
);

const TopReferrersTable = ({ limit = 25 }: TopReferrersTableProps) => {
  const fetchTopReferrers = async (offset = 0) => {
    const response = await getTopRewardsReferral({
      params: {
        limit,
        offset,
      },
    });

    return {
      items: response?.Users || [],
      count: response?.Count || 0,
    };
  };

  const {
    ref,
    initLoading,
    loadingMoreItems,
    totalItems,
    items: topReferrers,
  } = useScrollableTable({
    fetchItems: fetchTopReferrers,
    errorMessage: "An error happened when fetching top referrers",
  });

  if (initLoading) {
    return TopReferrersTable.fallback;
  }

  if (!totalItems) {
    return (
      <Fallback>
        <TableCell colSpan={4}>
          <p className="text-center">No top referrers found.</p>
        </TableCell>
      </Fallback>
    );
  }

  return (
    <>
      <div className="w-full overflow-hidden rounded-2xl border">
        <TableScrollable containerClassname="h-fit max-h-[510px] overflow-y-auto relative scrollable-table">
          <TableHeader>
            <TableRow>
              {TABLE_HEADERS.map(({ label }) => (
                <TableHead
                  key={label}
                  className={cn(
                    `
                      sticky top-0 z-200 cursor-pointer bg-background py-2
                      text-left text-xs text-muted
                      first:pl-4
                      md:text-sm
                    `,
                  )}
                >
                  {label}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>

          <TableBody>
            {(topReferrers || []).map((referrer, i) => (
              <TableRow key={i}>
                <TableCell className="w-[34%] p-0">
                  <div className="group flex items-center gap-2 p-2">
                    <Image
                      src="/assets/logo-deso-mark.svg"
                      width={15}
                      height={15}
                      alt="Deso"
                    />
                    <TopReferrerProfileAvatar
                      account={{
                        username: referrer.Username,
                        publicKey: referrer.PublicKeyBase58Check,
                      }}
                    />
                  </div>
                </TableCell>

                <TableCell
                  className="
                    font-shadow-green w-[22%] text-left font-mono text-xs
                    text-emerald-400
                    md:text-sm
                  "
                >
                  {formatUSDCents(referrer.TotalEarningsUsdCents)}
                </TableCell>

                <TableCell
                  className="
                    font-shadow-green w-[22%] text-left font-mono text-xs
                    text-emerald-400
                    md:text-sm
                  "
                >
                  {formatUSDCents(referrer.TotalReferralsUsdCents)}
                </TableCell>

                <TableCell
                  className="
                    font-shadow-green w-[22%] text-left font-mono text-xs
                    text-emerald-400
                    md:text-sm
                  "
                >
                  {formatUSDCents(referrer.TotalRevenueShareUsdCents)}
                </TableCell>
              </TableRow>
            ))}

            {((!initLoading && topReferrers.length !== totalItems) ||
              loadingMoreItems) && (
              <TableRow>
                <TableCell colSpan={4} ref={ref}>
                  <div className="flex justify-center gap-x-2">
                    <Spinner />
                    <span>Loading...</span>
                  </div>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </TableScrollable>
      </div>

      <div className="mt-4 text-center text-xs text-muted">
        Total: {pluralize(totalItems, "referrer")} discovered
      </div>
    </>
  );
};

TopReferrersTable.fallback = (
  <Fallback>
    {TABLE_HEADERS.map(({ label }) => (
      <TableCell key={label}>
        <Skeleton className="h-6 w-full" />
      </TableCell>
    ))}
  </Fallback>
);

export default TopReferrersTable;
